import type z from "zod";
import { type ZodObject } from "zod";
import { flattenObject, merge } from "./object";
import { getValidationPropertyType } from "./validation";

// Arrays of arrays have "comma" as a separator
// Arrays are repeated multiple times for each value
// Objects are flattened with dot notation
export function toQueryParams(obj: Record<string, unknown>) {
  const params = new URLSearchParams();
  const flattened = flattenObject(obj);

  for (const [key, value] of Object.entries(flattened)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (Array.isArray(value)) {
      value.forEach((el) => {
        if (Array.isArray(el)) {
          params.append(key, value.join(","));
        } else {
          params.append(key, String(el));
        }
      });
    } else {
      params.append(key, String(value));
    }
  }

  return params;
}

// sort=price,asc&sort=createdAt,desc&filter.category.$in=showers&filter.category.$in=tiles
export function parseQueryParams<T extends ZodObject>(
  searchParams: URLSearchParams,
  schema: T,
): z.infer<T> | null {
  let raw: Record<string, unknown> = {};

  try {
    for (const [dottedKey, value] of searchParams.entries()) {
      if (!value) continue;
      const slice = strToNestedObject(dottedKey, () => {
        const split = value.split(",");
        const valueWithoutComma = split.length === 1 ? value : split;
        const propertyType = getValidationPropertyType(schema, dottedKey);

        switch (propertyType) {
          case "number":
          case "number[]":
            const numValue = parseInt(valueWithoutComma as string, 10);
            const value = isNaN(numValue) ? undefined : numValue;
            return propertyType.includes("[]") ? [value] : value;
          case "string":
            return valueWithoutComma;
          case "string[]":
            return [valueWithoutComma];
        }
      });

      raw = merge(raw, slice);
    }

    const validInstance = (schema.loose() as T).parse(raw);
    return validInstance;
  } catch (_e: unknown) {
    return null;
  }
}

// filter.category.$in=showers => { filter: { category: { $in: "showers" } } }
function strToNestedObject(dottedKeys: string, getValue: () => unknown) {
  const parts = dottedKeys.split(".");
  const root: Record<string, unknown> = {};
  let curr = root;

  for (let i = 0; i < parts.length; i++) {
    const key = parts[i];

    if (i === parts.length - 1) {
      curr[key] = getValue();
    } else {
      curr[key] ??= {};
      curr = curr[key] as Record<string, unknown>;
    }
  }

  return root;
}

export function objectToURLSearchParams(params: { [key: string]: string | string[] | undefined }) {
  const urlParams = new URLSearchParams();

  for (const key in params) {
    const value = params[key];
    if (typeof value === "string") {
      urlParams.append(key, value);
    } else if (Array.isArray(value)) {
      value.forEach((item) => urlParams.append(key, item));
    }
    // undefined/null are skipped
  }
  return urlParams;
}
